/**
 * Condition-specific templates (`BACKLOG.md` M7): the five conditions named in the module brief —
 * fracture, joint replacement, ACL, rotator cuff, carpal tunnel — each with its own small set of
 * structured fields a clinician fills in for that condition, distinct from the generic problem-list
 * entry `clinical.ts`'s `problems` table already holds (an ICD-10 code and free text). A condition
 * episode row (`db/schema/orthopedics.ts`'s `orthopedicsConditionEpisodes`) carries one `conditionType`
 * from the closed set below plus a `templateFields` JSON object whose shape this module documents.
 *
 * **Deliberately not a fully normalized table per condition.** Five narrow, rarely-queried template
 * shapes would mean five near-empty tables, each needing its own migration to add a field — the same
 * over-normalization `docs/comparison-emrx.md` warns against for content this shape. `templateFields`
 * is validated at the application boundary (`orthopedicsRequests.ts`'s per-condition Zod schema) against
 * exactly the field set documented here, so an unrecognised or missing field is a `400`, not a silent
 * gap — the open-but-checked discipline this schema already uses for every other closed value set
 * (`bodyMapping.ts`'s own header names the same tradeoff).
 *
 * Every field below is a structural/administrative descriptor (a side, a laterality, a fixation type,
 * a graft choice) — none of it is copyrighted outcome-instrument content, so this module carries none
 * of `patientReportedOutcomes.ts`'s licensing caveat. It still needs the same clinical-review caveat as
 * `examManeuvers.ts`: the specific field lists below are a reasonable structured-note starting point,
 * not a validated template sourced from a real EHR's own ortho documentation — **UNREVIEWED**, note
 * this comment before treating the exact field set as settled practice.
 */

export const CONDITION_TYPES = [
  'fracture',
  'joint_replacement',
  'acl',
  'rotator_cuff',
  'carpal_tunnel',
] as const;
export type ConditionType = (typeof CONDITION_TYPES)[number];

export function isConditionType(value: string): value is ConditionType {
  return (CONDITION_TYPES as readonly string[]).includes(value);
}

const CONDITION_TYPE_LABELS: Readonly<Record<ConditionType, string>> = {
  fracture: 'Fracture',
  joint_replacement: 'Joint replacement',
  acl: 'ACL',
  rotator_cuff: 'Rotator cuff',
  carpal_tunnel: 'Carpal tunnel',
};

/** A human-readable label for a condition type — English-only domain vocabulary, matching
 * `aesthetics/injectables.ts`'s `injectableProductClassLabel` precedent for a closed clinical value
 * set that gets a shared label function rather than a per-locale translation key. */
export function conditionTypeLabel(conditionType: ConditionType): string {
  return CONDITION_TYPE_LABELS[conditionType];
}

/** One template field's descriptor: a stable key, a label for a UI, and — for a closed field — the
 * set of values it accepts. A field with no `values` is free text (e.g. a fracture's AO/OTA code),
 * validated only for length at the request-schema layer. */
export interface TemplateFieldDescriptor {
  readonly key: string;
  readonly label: string;
  readonly values?: readonly string[];
}

/**
 * Per-condition field lists. **UNREVIEWED** — see this module's header. Each is intentionally small:
 * enough to make a condition episode more than a bare diagnosis code, not an attempt at a complete
 * structured note.
 */
export const CONDITION_TEMPLATE_FIELDS: Readonly<
  Record<ConditionType, readonly TemplateFieldDescriptor[]>
> = {
  fracture: [
    { key: 'aoOtaCode', label: 'AO/OTA fracture classification code' },
    {
      key: 'displacement',
      label: 'Displacement',
      values: ['nondisplaced', 'displaced', 'comminuted'],
    },
    { key: 'openFracture', label: 'Open fracture', values: ['true', 'false'] },
    {
      key: 'treatmentApproach',
      label: 'Treatment approach',
      values: ['closed_reduction', 'open_reduction_internal_fixation', 'casting_only'],
    },
  ],
  joint_replacement: [
    { key: 'joint', label: 'Joint', values: ['hip', 'knee', 'shoulder'] },
    { key: 'approach', label: 'Surgical approach' },
    {
      key: 'implantType',
      label: 'Implant type',
      values: ['total', 'partial_hemiarthroplasty', 'revision'],
    },
    { key: 'fixation', label: 'Fixation', values: ['cemented', 'cementless', 'hybrid'] },
  ],
  acl: [
    {
      key: 'graftType',
      label: 'Graft type',
      values: [
        'autograft_patellar_tendon',
        'autograft_hamstring',
        'autograft_quad_tendon',
        'allograft',
      ],
    },
    {
      key: 'concomitantMeniscalRepair',
      label: 'Concomitant meniscal repair',
      values: ['true', 'false'],
    },
    {
      key: 'mechanism',
      label: 'Injury mechanism',
      values: ['pivot', 'contact', 'noncontact', 'other'],
    },
  ],
  rotator_cuff: [
    {
      key: 'tornTendons',
      label: 'Torn tendon(s)',
      values: ['supraspinatus', 'infraspinatus', 'subscapularis', 'teres_minor'],
    },
    { key: 'tearThickness', label: 'Tear thickness', values: ['partial', 'full_thickness'] },
    {
      key: 'repairApproach',
      label: 'Repair approach',
      values: ['arthroscopic', 'open', 'nonoperative'],
    },
  ],
  carpal_tunnel: [
    {
      key: 'severity',
      label: 'Electrodiagnostic severity',
      values: ['mild', 'moderate', 'severe', 'not_tested'],
    },
    {
      key: 'treatmentApproach',
      label: 'Treatment approach',
      values: ['splinting', 'injection', 'open_release', 'endoscopic_release'],
    },
  ],
};

/** The field descriptor for `(conditionType, key)`, or `undefined` if `key` is not part of that
 * condition's template — the one place a caller checks whether a value is closed and, if so, what
 * it may be. */
export function templateFieldFor(
  conditionType: ConditionType,
  key: string,
): TemplateFieldDescriptor | undefined {
  return CONDITION_TEMPLATE_FIELDS[conditionType].find((field) => field.key === key);
}
